/**
 * TeenPattiBettingControls — Action buttons and raise slider.
 */
import React, { useState } from 'react';
import { TeenPattiBettingControlsProps, TeenPattiAction } from '../types';
import './TeenPattiBettingControls.css';

const TeenPattiBettingControls: React.FC<TeenPattiBettingControlsProps> = ({
  validActions, pot, currentStake, minBet, maxBet, playerStack,
  isBlind, onAction, disabled,
}) => {
  const [raiseAmount, setRaiseAmount] = useState(minBet);
  const [showRaiseSlider, setShowRaiseSlider] = useState(false);

  const hasActionType = (type: string) => validActions.some(a => a.type === type);
  const getAction = (type: string) => validActions.find(a => a.type === type);

  const handleAction = (action: TeenPattiAction) => {
    if (disabled) return;
    setShowRaiseSlider(false);
    onAction(action);
  };

  const callAction = getAction('call');
  const raiseAction = getAction('raise');
  const effectiveMin = raiseAction?.minAmount || minBet;
  const effectiveMax = Math.min(raiseAction?.maxAmount || maxBet, playerStack);

  return (
    <div className="tp-betting" role="group" aria-label="Betting controls">
      <div className="tp-betting__info">
        <span className="tp-betting__pot">Pot: {pot}</span>
        <span className="tp-betting__stake">Stake: {currentStake}</span>
        <span className="tp-betting__stack">Chips: {playerStack}</span>
        <span className={`tp-betting__status ${isBlind ? 'tp-betting__status--blind' : 'tp-betting__status--seen'}`}>
          {isBlind ? '🔒 Blind' : '👁 Seen'}
        </span>
      </div>

      <div className="tp-betting__actions">
        {hasActionType('pack') && (
          <button
            className="tp-betting__btn tp-betting__btn--pack"
            onClick={() => handleAction({ type: 'pack' })}
            disabled={disabled}
            aria-label="Pack (fold)"
          >
            Pack
          </button>
        )}

        {hasActionType('call') && (
          <button
            className="tp-betting__btn tp-betting__btn--call"
            onClick={() => handleAction({ type: 'call' })}
            disabled={disabled}
            aria-label={`Call ${callAction?.callAmount || ''}`}
          >
            Call {callAction?.callAmount || ''}
          </button>
        )}

        {hasActionType('raise') && (
          <button
            className="tp-betting__btn tp-betting__btn--raise"
            onClick={() => setShowRaiseSlider(!showRaiseSlider)}
            disabled={disabled}
            aria-label="Raise"
          >
            Raise
          </button>
        )}

        {hasActionType('see-cards') && (
          <button
            className="tp-betting__btn tp-betting__btn--see"
            onClick={() => handleAction({ type: 'see-cards' })}
            disabled={disabled}
            aria-label="See your cards"
          >
            👁 See Cards
          </button>
        )}

        {hasActionType('sideshow') && (
          <button
            className="tp-betting__btn tp-betting__btn--sideshow"
            onClick={() => handleAction({ type: 'sideshow' })}
            disabled={disabled}
            aria-label="Request sideshow"
          >
            Sideshow
          </button>
        )}

        {hasActionType('show') && (
          <button
            className="tp-betting__btn tp-betting__btn--show"
            onClick={() => handleAction({ type: 'show' })}
            disabled={disabled}
            aria-label="Show cards"
          >
            Show
          </button>
        )}
      </div>

      {showRaiseSlider && hasActionType('raise') && (
        <div className="tp-betting__raise-panel" role="group" aria-label="Raise amount">
          <input
            type="range"
            className="tp-betting__slider"
            min={effectiveMin}
            max={effectiveMax}
            value={raiseAmount}
            onChange={e => setRaiseAmount(Number(e.target.value))}
            aria-label="Raise amount slider"
          />
          <div className="tp-betting__raise-info">
            <span>{effectiveMin}</span>
            <span className="tp-betting__raise-value">{raiseAmount}</span>
            <span>{effectiveMax}</span>
          </div>
          <button
            className="tp-betting__btn tp-betting__btn--confirm"
            onClick={() => handleAction({ type: 'raise', amount: raiseAmount })}
            disabled={disabled}
            aria-label={`Confirm raise of ${raiseAmount}`}
          >
            Raise {raiseAmount}
          </button>
        </div>
      )}
    </div>
  );
};

export { TeenPattiBettingControls };
export default TeenPattiBettingControls;
